import { FormControl, FormHelperText, FormLabel, Grid, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import clsx from 'clsx';
import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';

import StyledDropzone from 'app/components/Dropzone';
import { translations } from 'locales/i18n';

export default function Offer() {
  const { t } = useTranslation();

  const { setFieldValue, values, errors, touched, setFieldTouched }: any = useFormikContext();

  return (
    <Grid container>
      <Grid container>
        <FormControl className="listingModal__body__input" required fullWidth>
          <FormLabel className="listingModal__body__input__label">
            {t(translations.common.title())}
          </FormLabel>
          <Field
            as={TextField}
            name="title"
            variant="outlined"
            fullWidth
            error={touched.title && errors.title}
            helperText={touched.title && errors.title}
            required
          />
        </FormControl>
        <FormControl className="listingModal__body__input" required fullWidth>
          <FormLabel className="listingModal__body__input__label">
            {t(translations.pages.listings.create.offer.offerText())}
          </FormLabel>
          <Field name="offerText" error={touched.offerText && errors.offerText}>
            {({ field }) => (
              <>
                {
                  <ReactQuill
                    className={clsx('listingModal__body__input__rte', {
                      'listingModal__body__input__rte--error': touched.offerText && errors.offerText
                    })}
                    value={field.value}
                    onChange={field.onChange(field.name)}
                    onBlur={(prev, source, editor) => {
                      if (!touched.offerText) {
                        setFieldTouched('offerText', true);
                      }
                      const content = editor.getHTML();
                      setFieldValue('offerText', content.replace(/<p><b><br><\/p>/g, ''));
                    }}
                    theme="snow"
                    modules={{
                      toolbar: [
                        ['bold', 'italic'],
                        [
                          { list: 'ordered' },
                          { list: 'bullet' },
                          { indent: '-1' },
                          { indent: '+1' }
                        ],
                        ['clean']
                      ],
                      clipboard: {
                        allowed: {
                          tags: [
                            'a',
                            'b',
                            'strong',
                            'u',
                            's',
                            'i',
                            'p',
                            'br',
                            'ul',
                            'ol',
                            'li',
                            'span'
                          ],
                          attributes: ['href', 'rel', 'target', 'class']
                        },
                        keepSelection: true,
                        substituteBlockElements: true,
                        magicPasteLinks: true,
                        matchVisual: true
                      }
                    }}
                    formats={[
                      'bold',
                      'italic',
                      'list',
                      'bullet',
                      'indent',
                      'link',
                      'align',
                      'header',
                      'size',
                      'font',
                      'color',
                      'header',
                      'underline'
                    ]}
                  />
                }
                <FormHelperText>
                  {t(translations.pages.listings.create.offer.minCharsHelper())}
                </FormHelperText>
                {touched.offerText && errors.offerText && (
                  <FormHelperText error>{errors.offerText}</FormHelperText>
                )}
              </>
            )}
          </Field>
        </FormControl>
        <FormControl className="listingModal__body__input" fullWidth>
          <FormLabel className="listingModal__body__input__label">
            {t(translations.pages.listings.create.offer.address())}
          </FormLabel>
          <Field
            as={TextField}
            name="address"
            variant="outlined"
            fullWidth
            error={touched.address && errors.address}
            helperText={touched.address && errors.address}
            multiline
            rowsMax={3}
          />
        </FormControl>
        <FormControl className="listingModal__body__input" fullWidth>
          <FormLabel className="listingModal__body__input__label">
            {t(translations.pages.listings.create.offer.photos())}
          </FormLabel>
        </FormControl>
        <StyledDropzone
          name="photos"
          setFieldValue={setFieldValue}
          value={values.photos}
          errors={errors}
          accept={{
            'image/jpeg': ['.jfif', '.jfif-tbnl', '.jpe', '.jpeg', '.jpg'],
            'image/png': ['.png', '.x-png'],
            'image/svg+xml': ['.svg']
          }}
          placeholder={t(translations.pages.listings.create.offer.photosPlaceholder())}
          className="listingModal__body__input__dropzone"
          multiple
        />
        <Grid container>
          <FormControl className="listingModal__body__input" fullWidth>
            <FormLabel className="listingModal__body__input__label">
              {t(translations.pages.listings.create.offer.lifetime())}
            </FormLabel>
            {values.type !== 'event' && (
              <FormHelperText className="listingModal__body__input__helper">
                {t(translations.pages.listings.create.offer.lifetimeDescription())}
              </FormHelperText>
            )}
          </FormControl>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <DatePicker
                className="listingModal__body__input__datepicker"
                renderInput={props => (
                  <TextField
                    {...props}
                    variant="outlined"
                    label={t(translations.pages.listings.create.offer.startDate())}
                  />
                )}
                inputFormat="dd.MM.yyyy"
                value={values.startDate}
                onChange={e => setFieldValue('startDate', e)}
                maxDate={values.endDate || undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                className="listingModal__body__input__datepicker"
                disablePast
                renderInput={props => (
                  <TextField
                    {...props}
                    variant="outlined"
                    label={t(translations.pages.listings.create.offer.endDate())}
                  />
                )}
                inputFormat="dd.MM.yyyy"
                value={values.endDate}
                onChange={e => setFieldValue('endDate', e)}
                minDate={values.startDate}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
